'use client';

import React from 'react';
import IndustryCard, { IndustryCardData } from './IndustryCard';
import ROICalculator from './ROICalculator';
import ComplianceMatrix from './ComplianceMatrix';
import FacilityLayoutTool from './FacilityLayoutTool';

const IndustryApplicationsInteractive: React.FC = () => {
  const industries: IndustryCardData[] = [
  {
    id: 'manufacturing',
    title: 'Manufacturing',
    description: 'Comprehensive AI monitoring for production floors, assembly lines, and quality control areas. Ensure PPE compliance, detect unsafe behaviors, and maintain operational efficiency across your manufacturing facility.',
    image: "https://images.unsplash.com/photo-1639392653596-06f3723d6920",
    alt: 'Modern manufacturing facility with automated assembly line and robotic equipment in clean industrial environment',
    icon: 'CogIcon',
    challenges: [
    'PPE compliance across multiple shifts',
    'Machine safety zone violations',
    'Quality control consistency',
    'Unauthorized access to restricted areas'],

    solutions: [
    'Real-time PPE detection and alerts',
    'Safety zone monitoring with instant notifications',
    'Behavioral pattern analysis for risk prevention',
    'Automated compliance reporting for audits'],

    metrics: [
    { label: 'Incident Reduction', value: '65%' },
    { label: 'Compliance Rate', value: '98%' },
    { label: 'ROI Timeline', value: '8 months' }],

    caseStudyLink: '/contact'
  },
  {
    id: 'construction',
    title: 'Construction',
    description: 'Dynamic site monitoring adapting to changing construction environments. Track PPE usage, monitor high-risk activities, and ensure contractor compliance across active construction sites.',
    image: "https://images.unsplash.com/photo-1489256784917-357a8e0f304d",
    alt: 'Active construction site with workers wearing safety helmets and high-visibility vests operating heavy machinery',
    icon: 'WrenchScrewdriverIcon',
    challenges: [
    'Dynamic site layouts and hazards',
    'Multiple contractor coordination',
    'Fall protection compliance',
    'Equipment operation safety'],

    solutions: [
    'Adaptive AI learning for changing environments',
    'Multi-contractor tracking and reporting',
    'Height work monitoring and fall detection',
    'Heavy equipment proximity alerts'],

    metrics: [
    { label: 'Incident Reduction', value: '70%' },
    { label: 'Compliance Rate', value: '96%' },
    { label: 'ROI Timeline', value: '6 months' }],

    caseStudyLink: '/contact'
  },
  {
    id: 'mining',
    title: 'Mining',
    description: 'Extreme environment monitoring for underground and surface mining operations. Ensure worker safety in hazardous conditions, monitor equipment operations, and maintain regulatory compliance.',
    image: "https://images.unsplash.com/photo-1726554210101-950c365ca9b4",
    alt: 'Underground mining tunnel with industrial lighting and heavy excavation equipment in operation',
    icon: 'CubeIcon',
    challenges: [
    'Low visibility conditions',
    'Hazardous material handling',
    'Remote location monitoring',
    'MSHA compliance requirements'],

    solutions: [
    'Low-light AI vision technology',
    'Hazmat PPE verification systems',
    'LTE-based remote monitoring',
    'Automated MSHA reporting integration'],

    metrics: [
    { label: 'Incident Reduction', value: '75%' },
    { label: 'Compliance Rate', value: '99%' },
    { label: 'ROI Timeline', value: '7 months' }],

    caseStudyLink: '/contact'
  },
  {
    id: 'logistics',
    title: 'Logistics & Warehousing',
    description: 'High-volume facility monitoring for warehouses, distribution centers, and logistics hubs. Track forklift operations, loading dock safety, and inventory security with AI-powered surveillance.',
    image: "https://images.unsplash.com/photo-1689942007186-099d175dd491",
    alt: 'Large modern warehouse with organized inventory shelves and forklift operators moving pallets',
    icon: 'TruckIcon',
    challenges: [
    'High-traffic loading dock safety',
    'Forklift operation monitoring',
    'Theft prevention and inventory security',
    'Multi-shift workforce management'],

    solutions: [
    'Loading zone safety monitoring',
    'Forklift proximity detection and alerts',
    'AI-powered theft detection systems',
    '24/7 automated surveillance coverage'],

    metrics: [
    { label: 'Incident Reduction', value: '60%' },
    { label: 'Theft Prevention', value: '85%' },
    { label: 'ROI Timeline', value: '9 months' }],

    caseStudyLink: '/contact'
  },
  {
    id: 'energy',
    title: 'Energy & Utilities',
    description: 'Critical infrastructure protection for power plants, refineries, and utility facilities. Monitor high-voltage areas, ensure arc flash protection compliance, and secure perimeter access.',
    image: "https://images.unsplash.com/photo-1706653332017-e4adcf38d55b",
    alt: 'Industrial power plant facility with cooling towers and electrical infrastructure at sunset',
    icon: 'BoltIcon',
    challenges: [
    'Arc flash protection compliance',
    'High-voltage area access control',
    'Critical infrastructure security',
    'Environmental monitoring requirements'],

    solutions: [
    'Arc flash PPE verification',
    'Restricted area access monitoring',
    'Perimeter intrusion detection',
    'Automated environmental compliance tracking'],

    metrics: [
    { label: 'Incident Reduction', value: '68%' },
    { label: 'Security Events', value: '92%' },
    { label: 'ROI Timeline', value: '10 months' }],

    caseStudyLink: '/contact'
  }];


  return (
    <div data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:144:4" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="144" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-0%22%7D" className="space-y-0">
      <section data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:145:6" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="145" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22className%22%3A%22py-16%20bg-background%22%7D" className="py-16 bg-background">
        <div data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:146:8" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="146" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:147:10" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="147" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20md%3Agrid-cols-2%20lg%3Agrid-cols-3%20gap-8%22%7D" className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry) =>
            <IndustryCard data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:149:12" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="149" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="IndustryCard" data-component-content="%7B%22elementName%22%3A%22IndustryCard%22%7D" key={industry.id} industry={industry} />
            )}
          </div>
        </div>
      </section>
      
      <ROICalculator data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:155:6" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="155" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="ROICalculator" data-component-content="%7B%22elementName%22%3A%22ROICalculator%22%7D" />
      {/* <ComplianceMatrix /> */}
      <FacilityLayoutTool data-component-id="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx:157:6" data-component-path="src/app/industry-applications/components/IndustryApplicationsInteractive.tsx" data-component-line="157" data-component-file="IndustryApplicationsInteractive.tsx" data-component-name="FacilityLayoutTool" data-component-content="%7B%22elementName%22%3A%22FacilityLayoutTool%22%7D" />
    </div>);

};

export default IndustryApplicationsInteractive;